import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { format, parseISO, isValid, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { IconWallet, IconPlus, IconEyeOpen, IconEyeClosed, IconMail, IconDownload } from './utils/icons';
import {
  __toYMD, monthLabel, getDerivedStatus, STATUS_OPTIONS,
  StatusPill, MoneyCell, SkeletonRow, EmptyState
} from './utils/helpers';
import { PaymentModal } from './modals/PaymentModal';
import { FilesModal } from './modals/FilesModal';

export function PaymentsView({ company, userRole }) {
  const { token } = useAuth();
  const isContador = (userRole ?? '').toString().toLowerCase() === 'contador';
  const isAdmin = (userRole ?? '').toString().toLowerCase() === 'admin';
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const [filesModalPayment, setFilesModalPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const proofInputRef = useRef({});
  const invoiceInputRef = useRef({});

  const [monthCursor, setMonthCursor] = useState(new Date());
  const [startDate, setStartDate]   = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate]       = useState(format(endOfMonth(new Date()),   'yyyy-MM-dd'));
  
  const [selectedStatuses, setSelectedStatuses] = useState(
    isContador 
      ? new Set(['pagado']) 
      : new Set(['atrasado','por_vencer','pendiente','pagado'])
  );

  useEffect(() => { if (isContador) setSelectedStatuses(new Set(['pagado'])); }, [isContador]);

  const [paidFrom, setPaidFrom] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportFrom, setReportFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [reportTo, setReportTo] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

  useEffect(() => { setPage(1); }, [paidFrom, paidTo]);
  useEffect(() => {
    const start = startOfMonth(monthCursor);
    const end   = endOfMonth(monthCursor);
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
    setPage(1);
  }, [monthCursor]);

  const fetchPayments = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({
        company: company,
        fromDate: startDate,
        toDate: endDate,
        sortBy: 'due_date',
        sortOrder: 'ASC',
        _: Date.now(),
      });
      if (searchTerm.trim()) {
        params.set('search', searchTerm.trim());
      }
      
      if (isContador) {
        params.set('status', 'pagado');
      }

      const url = `/api/owner/payments?${params.toString()}`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      if (response.ok) setPayments(data);
      else setError(data.mensaje || 'Error al cargar los pagos.');
    } catch { setError('Error de conexión al cargar los pagos.'); }
    finally { setLoading(false); }
  }, [company, token, startDate, endDate, searchTerm, isContador]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPayments();
    }, 350);
    return () => clearTimeout(timer);
  }, [fetchPayments]);

  const filteredSorted = useMemo(() => {
    const list = (payments || []).map(p => ({ ...p, _derived: getDerivedStatus(p) }));
    const active = selectedStatuses;
    const filtered = list.filter(p => {
      if (!active.has(p._derived)) return false;

      if (paidFrom || paidTo) {
        const isPaid = p._derived === 'pagado' || p.status === 'pagado';
        if (!isPaid || !p.paid_date) return false;
        const ymd = __toYMD(p.paid_date);
        if (!ymd) return false;
        if (paidFrom && ymd < paidFrom) return false;
        if (paidTo   && ymd > paidTo)   return false;
      }

      if (startDate || endDate) {
        const ymd = __toYMD(p.due_date);
        if (!ymd) {
           if (p._derived !== 'pagado') return false;
        } else {
          if (startDate && ymd < startDate) return false;
          if (endDate   && ymd > endDate)   return false;
        }
      }

      return true;
    });

    const rank = { atrasado: 0, por_vencer: 1, pendiente: 2, pagado: 3, sin_fecha: 2 };
    return filtered.sort((a, b) => {
      const ra = rank[a._derived] ?? 99;
      const rb = rank[b._derived] ?? 99;
      if (ra !== rb) return ra - rb;
      const da = a.due_date ? new Date(a.due_date) : new Date(0);
      const db = b.due_date ? new Date(b.due_date) : new Date(0);
      return da - db;
    });
  }, [payments, selectedStatuses, startDate, endDate, paidFrom, paidTo]);

  const PAGE_TOTAL = Math.max(1, Math.ceil(filteredSorted.length / PAGE_SIZE));
  const pageIdx = Math.min(page, PAGE_TOTAL);
  const sliceStart = (pageIdx - 1) * PAGE_SIZE;
  const pageItems = filteredSorted.slice(sliceStart, sliceStart + PAGE_SIZE);

  useEffect(() => { if (page > PAGE_TOTAL) setPage(PAGE_TOTAL); }, [PAGE_TOTAL, page]);

  const downloadCSV = (data) => {
    if (!data || data.length === 0) return;
    const headers = ['Empresa', 'Fecha de Pago', 'Título', 'Descripción/Nota', 'Monto', 'Moneda'];
    const csvRows = [headers.join(',')];
    
    const totals = data.reduce((acc, row) => {
       const curr = row.currency || 'MXN';
       acc[curr] = (acc[curr] || 0) + Number(row.amount || 0);
       return acc;
    }, {});

    for (const row of data) {
      const values = [
        `"${row.company || ''}"`,
        `"${row.paid_date || ''}"`,
        `"${(row.title || '').replace(/"/g, '""')}"`,
        `"${(row.note || '').replace(/"/g, '""')}"`,
        row.amount || 0,
        row.currency || 'MXN'
      ];
      csvRows.push(values.join(','));
    }
    
    csvRows.push([]);
    csvRows.push(['', '', '', 'TOTALES:', '', '']);
    for (const [currency, total] of Object.entries(totals)) {
       csvRows.push(['', '', '', `Total ${currency}`, 
         new Intl.NumberFormat('es-MX', { style: 'decimal', minimumFractionDigits: 2 }).format(total), 
         currency
       ]);
    }
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([`\uFEFF${csvString}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_pagos_${reportFrom}_a_${reportTo}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    setReportError('');
    setReportData(null);
    try {
      const params = new URLSearchParams({
        company: company,
        paid_from: reportFrom,
        paid_to: reportTo,
      });
      const url = `/api/owner/reports/payments?${params.toString()}`;
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.mensaje || 'Error al generar el reporte.');
      
      setReportData(data);
      if (data.length === 0) {
        setReportError('No se encontraron pagos pagados en ese rango de fechas.');
      }
    } catch (err) {
      setReportError(err.message || 'Error de conexión.');
    } finally {
      setReportLoading(false);
    }
  };

  const toggleStatus = (value) => {
    if (isContador) return;
    
    setSelectedStatuses(prev => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = parseISO(dateString);
    if (!isValid(date)) return 'N/A';
    return format(date, 'dd/MM/yyyy');
  };

  const openNewModal = () => { setSelectedPayment(null); setIsModalOpen(true); };

  const handleProofUpload = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('paymentProof', file);
    try {
      const r = await fetch(`/api/owner/payments/${id}/upload-proof`, { method:'POST', headers:{ 'Authorization': `Bearer ${token}` }, body: fd });
      if (r.ok) { fetchPayments(); } else { const d=await r.json(); alert(`Error: ${d.mensaje}`); }
    } catch { alert('Error de conexión.'); }
  };

  const handleToggleViewed = async (id) => {
    try {
      const response = await fetch(`/api/owner/payments/${id}/toggle-viewed`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('No se pudo actualizar el estado.');
      }
      fetchPayments();
    } catch (err) {
      alert(err.message || 'Error de conexión.');
    }
  };

  const handleInvoicesUpload = async (id, files) => {
    if (!files || !files.length) return;
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('invoices', f));
    try {
      const r = await fetch(`/api/owner/payments/${id}/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (r.ok) fetchPayments();
      else {
        const d = await r.json().catch(()=>({}));
        alert(`Error: ${d.mensaje || 'No se pudieron subir las facturas'}`);
      }
    } catch { alert('Error de conexión.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    try {
      const r = await fetch(`/api/owner/payments/${id}`, { method:'DELETE', headers:{ 'Authorization': `Bearer ${token}` }});
      if (r.ok) fetchPayments(); else alert('Error al eliminar.');
    } catch { alert('Error de conexión.'); }
  };

  const total = filteredSorted.length;
  const totalPages = PAGE_TOTAL;

  const openFilesModal = (p) => { setFilesModalPayment(p); setFilesModalOpen(true); };

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
            <IconWallet className="h-4 w-4" /> Pagos
          </div>
          <div className="text-slate-500 text-sm">
            {isContador
              ? 'Consulta los pagos realizados y exporta reportes.'
              : 'Gestiona tus cuentas por pagar y sus comprobantes'
            }
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
             <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
             </svg>
             <input
               type="text"
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               placeholder="Buscar por título o nota..."
               className="w-full rounded-xl border-slate-200 pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-slate-900/20"
             />
          </div>
          
          {isAdmin && (
            <button onClick={openNewModal}
              className="group inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2 shadow-lg hover:shadow-xl transition focus:ring-4 focus:ring-slate-900/20 whitespace-nowrap">
              <span className="relative flex h-5 w-5 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/30 opacity-60"></span>
                <IconPlus className="relative h-4 w-4" />
              </span>
              Nuevo pago
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-slate-200/70 bg-white/70 p-4 md:grid-cols-12">
        <div className="md:col-span-5 flex items-center gap-2">
          <button
            onClick={() => setMonthCursor(prev => addMonths(prev, -1))}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            ← Mes anterior
          </button>
          <div className="px-3 py-1.5 text-sm font-semibold text-slate-800">{monthLabel(monthCursor)}</div>
          <button
            onClick={() => setMonthCursor(prev => addMonths(prev, 1))}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Mes siguiente →
          </button>
          <button
            onClick={() => setMonthCursor(new Date())}
            className="ml-2 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:shadow"
          >
            Hoy
          </button>
        </div>

        <div className="md:col-span-4 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600">Vence Desde</label>
            <input
              type="date"
              value={startDate}
              min={format(startOfMonth(monthCursor), 'yyyy-MM-dd')}
              max={endDate}
              onChange={(e)=>{ setStartDate(e.target.value); setPage(1); }}
              className="mt-1 w-full rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">Vence Hasta</label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={format(endOfMonth(monthCursor), 'yyyy-MM-dd')}
              onChange={(e)=>{ setEndDate(e.target.value); setPage(1); }}
              className="mt-1 w-full rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/10"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-600">
              Pagados entre
            </label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <input
                type="date"
                value={paidFrom}
                onChange={(e) => { setPaidFrom(e.target.value); setPage(1); }}
                className="w-full rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/10"
                placeholder="Desde"
              />
              <input
                type="date"
                value={paidTo}
                onChange={(e) => { setPaidTo(e.target.value); setPage(1); }}
                className="w-full rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/10"
                placeholder="Hasta"
              />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setPaidFrom(''); setPaidTo(''); setPage(1); }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                Limpiar
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Filtra <b>pagados</b> por fecha de pago.
            </p>
          </div>
        </div>

        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">Estatus</label>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map(opt => {
              if (isContador && opt.value !== 'pagado') {
                return null;
              }
              return (
                <label key={opt.value}
                       className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs ${
                         selectedStatuses.has(opt.value)
                           ? 'border-slate-300 bg-slate-100 text-slate-800'
                           : 'border-slate-200 bg-white text-slate-600'
                       } ${isContador ? 'opacity-70 cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedStatuses.has(opt.value)}
                    onChange={()=>toggleStatus(opt.value)}
                    disabled={isContador}
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl border border-slate-200/70 bg-white/70 p-4 md:grid-cols-12">
        <div className="md:col-span-3">
          <h4 className="text-sm font-semibold text-slate-800">Reporte de Pagos (CSV)</h4>
          <p className="text-xs text-slate-500">Genera un reporte de pagos <b>pagados</b> en un rango de fechas.</p>
          <p className="mt-1 text-xs font-semibold text-slate-600">
            Empresa para el reporte: <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-800">{company === 'TODAS' ? 'Todas las empresas' : company}</span>
          </p>
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-slate-600">Pagado Desde</label>
          <input
            type="date"
            value={reportFrom}
            onChange={(e) => setReportFrom(e.target.value)}
            className="mt-1 w-full rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/10"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-slate-600">Pagado Hasta</label>
          <input
            type="date"
            value={reportTo}
            onChange={(e) => setReportTo(e.target.value)}
            className="mt-1 w-full rounded-lg border-slate-200 focus:ring-2 focus:ring-slate-900/10"
          />
        </div>
        <div className="md:col-span-3 flex items-end gap-2">
          <button
            onClick={handleGenerateReport}
            disabled={reportLoading}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:shadow disabled:opacity-50"
          >
            {reportLoading ? 'Generando...' : 'Generar Vista Previa'}
          </button>
          <button
            onClick={() => downloadCSV(reportData)}
            disabled={!reportData || reportData.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
          >
            <IconDownload />
            Descargar CSV
          </button>
        </div>
        {(reportError || (reportData && !reportLoading)) && (
          <div className="md:col-span-12 mt-2 text-sm">
            {reportError && <span className="text-rose-600">{reportError}</span>}
            {reportData && !reportError && (
              <span className="text-emerald-700">
                Reporte generado con {reportData.length} registro(s). Listo para descargar.
              </span>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200/70 bg-white/70 backdrop-blur-sm shadow-sm">
        <div className="max-h-[62vh] overflow-auto rounded-2xl">
          <table className="min-w-full">
            <thead className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur supports-[backdrop-filter]:bg-slate-50/60">
              <tr className="text-left text-xs font-semibold tracking-wide text-slate-600">
                {company === 'TODAS' && <th className="px-6 py-3">Empresa</th>}
                <th className="px-6 py-3">Título</th>
                <th className="px-6 py-3">Categoría</th>
                <th className="px-6 py-3">Monto</th>
                <th className="px-6 py-3">Vencimiento</th>
                <th className="px-6 py-3">Fecha de Pago</th>
                <th className="px-6 py-3">Estado</th>
                <th className="px-6 py-3">Visto</th>
                <th className="px-6 py-3">Facturas</th>
                <th className="px-6 py-3">Comprobante</th>
                {isAdmin && (
                  <th className="px-6 py-3 text-right">Acciones</th>
                )}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading && (
                <>
                  <SkeletonRow cols={company === 'TODAS' ? 11 : 10} />
                  <SkeletonRow cols={company === 'TODAS' ? 11 : 10} />
                  <SkeletonRow cols={company === 'TODAS' ? 11 : 10} />
                </>
              )}

              {!loading && pageItems.length === 0 && (
                <tr>
                  <td colSpan={company === 'TODAS' ? 11 : 10}>
                    <EmptyState onNew={openNewModal} userRole={userRole} />
                  </td>
                </tr>
              )}

              {!loading && pageItems.map(p => {
                const invoices = p.attachments || [];
                const firstThree = invoices.slice(0,3);
                const extra = Math.max(0, invoices.length - 3);

                return (
                  <tr key={p.id} className={`transition hover:bg-slate-50 ${p.is_viewed ? 'bg-slate-50/50' : ''}`}>
                    {company === 'TODAS' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                          {p.company}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-slate-800">{p.title}</div>
                      {p.note ? <div className="text-xs text-slate-500 truncate max-w-[28ch]">{p.note}</div> : null}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-600">{p.category}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <MoneyCell amount={p.amount} currency={p.currency} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-slate-700">{formatDate(p.due_date)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-600">
                        {p.status === 'pagado' ? formatDate(p.paid_date) : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusPill status={getDerivedStatus(p)} due_date={p.due_date} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {p.status === 'pagado' ? (
                        isAdmin ? (
                          <button 
                            onClick={() => handleToggleViewed(p.id)} 
                            className="p-2 rounded-full hover:bg-slate-200 transition-colors"
                            title={p.is_viewed ? 'Marcar como no visto' : 'Marcar como visto'}
                          >
                            {p.is_viewed ? (
                              <IconEyeOpen className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <IconEyeClosed className="w-5 h-5 text-slate-400" />
                            )}
                          </button>
                        ) : (
                          <span className="p-2 inline-block">
                            {p.is_viewed ? (
                              <IconEyeOpen className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <IconEyeClosed className="w-5 h-5 text-slate-400" />
                            )}
                          </span>
                        )
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 flex-wrap max-w-[28ch]">
                        {firstThree.map(a => (
                          <a key={a.id} href={`/uploads/${(a.file_path || '').replace(/#/g, '%23')}`} target="_blank" rel="noopener noreferrer"
                            className="rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-700 underline decoration-slate-300">
                            {a.file_name.length > 14 ? a.file_name.slice(0,14)+'…' : a.file_name}
                          </a>
                        ))}
                        {extra > 0 && (
                          <button
                            onClick={()=>openFilesModal(p)}
                            className="rounded-lg bg-white border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Ver todos (+{extra})
                          </button>
                        )}
                        
                        {isAdmin && (
                          <>
                            <input
                              type="file"
                              accept="application/pdf,image/*"
                              multiple
                              style={{ display: 'none' }}
                              ref={el => invoiceInputRef.current[p.id] = el}
                              onChange={(e)=>handleInvoicesUpload(p.id, e.target.files)}
                            />
                            <button
                              onClick={()=>invoiceInputRef.current[p.id].click()}
                              className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                            >
                              { invoices.length ? 'Añadir' : 'Subir' }
                            </button>
                          </>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-2 max-w-[28ch]">
                        {p.status === 'pagado' ? (
                          p.proof_file_path ? (
                            <>
                              <a href={`/uploads/${(p.proof_file_path || '').replace(/#/g, '%23')}`} target="_blank" rel="noopener noreferrer"
                                className="text-sm font-medium text-slate-700 underline decoration-slate-300 hover:text-slate-900">
                                Ver comprobante
                              </a>
                              
                              {isAdmin && p.provider_email && (
                                <button
                                  onClick={async () => {
                                    if(!window.confirm(`¿Enviar comprobante a ${p.provider_email}?`)) return;
                                    try {
                                      const r = await fetch(`/api/owner/payments/${p.id}/send-email`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      const d = await r.json();
                                      if(r.ok) alert(d.mensaje);
                                      else alert('Error: ' + d.mensaje);
                                    } catch (e) {
                                      alert('Error de conexión al enviar correo');
                                    }
                                  }}
                                  className="inline-flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1.5 rounded-lg border border-slate-200 transition-colors"
                                  title={`Enviar a: ${p.provider_email}`}
                                >
                                  <IconMail className="h-3 w-3" /> Enviar email
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              {isAdmin && (
                                <>
                                  <input
                                    type="file" accept="application/pdf" style={{ display: 'none' }}
                                    ref={el => proofInputRef.current[p.id] = el}
                                    onChange={(e)=>handleProofUpload(p.id, e.target.files[0])}
                                  />
                                  <button onClick={()=>proofInputRef.current[p.id].click()}
                                    className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200">
                                    Subir PDF
                                  </button>
                                </>
                              )}
                            </>
                          )
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}

                        {(invoices.length + (p.proof_file_path ? 1 : 0)) > 3 && (
                          <button
                            onClick={()=>openFilesModal(p)}
                            className="rounded-lg bg-white border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 mt-1"
                          >
                            Ver todos (+{(invoices.length + (p.proof_file_path ? 1 : 0)) - 3})
                          </button>
                        )}
                      </div>
                    </td>

                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-2">
                          {p.status !== 'pagado' && (
                            <button onClick={async () => {
                              const r = await fetch(`/api/owner/payments/${p.id}/mark-paid`, { method:'PUT', headers:{ 'Authorization': `Bearer ${token}` }});
                              if (r.ok) fetchPayments(); else alert('Error al marcar como pagado.');
                            }}
                                    className="rounded-lg bg-emerald-600/90 text-white px-3 py-1.5 text-sm font-semibold hover:bg-emerald-700 shadow-sm">
                              Pagar
                            </button>
                          )}
                          <button onClick={() => { setSelectedPayment(p); setIsModalOpen(true); }}
                                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                            Editar
                          </button>
                          <button onClick={() => handleDelete(p.id)}
                                  className="rounded-lg bg-rose-50 text-rose-700 px-3 py-1.5 text-sm font-semibold hover:bg-rose-100">
                            Eliminar
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}

            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4">
            <div className="text-sm text-slate-600">
              Mostrando <span className="font-semibold text-slate-800">{Math.min(sliceStart + 1, total)}</span>–<span className="font-semibold text-slate-800">{Math.min(sliceStart + pageItems.length, total)}</span> de <span className="font-semibold text-slate-800">{total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={()=> setPage(p => Math.max(1, p-1))}
                disabled={pageIdx === 1}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:opacity-40"
              >
                ← Anterior
              </button>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={pageIdx}
                onChange={(e)=> setPage(Math.min(Math.max(1, Number(e.target.value) || 1), totalPages))}
                className="w-16 rounded-lg border-slate-200 text-center"
              />
              <span className="text-sm text-slate-600">de {totalPages}</span>
              <button
                onClick={()=> setPage(p => Math.min(totalPages, p+1))}
                disabled={pageIdx === totalPages}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:opacity-40"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && isAdmin && (
        <PaymentModal
          payment={selectedPayment}
          onClose={() => setIsModalOpen(false)}
          onSave={fetchPayments}
          company={company}
        />
      )}

      {filesModalOpen && filesModalPayment && (
        <FilesModal
          payment={filesModalPayment}
          onClose={()=>{ setFilesModalOpen(false); setFilesModalPayment(null); }}
          onUploadInvoices={handleInvoicesUpload}
          onUploadProof={handleProofUpload}
          userRole={userRole}
        />
      )}
    </div>
  );
}
